import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import Interweave from "interweave";
import { UrlMatcher, EmailMatcher } from "interweave-autolink";
import { useAuth } from "firebase";
import { MentionsMatcher } from "./matchers";
import {
  CommentArrow,
  EmojiClap,
  EmojiLike,
  EmojiLove,
  EmojiSmile,
} from "images/svgs";
import { ThumbsUp, Pin } from "images/animated-svgs";
import { fadeInAndOutVariants } from "styles";
import { useWindowSize } from "hooks";

const Chat = () => {
  const { windowWidth } = useWindowSize();
  const isMobile = windowWidth < 1150;
  const [participate, setParticipate] = useState(false);
  const [allComments, setAllComments] = useState([]);
  const [allCommentsToGenerateCSV, setAllCommentsToGenerateCSV] = useState([]);
  const [showEmojisSelector, setShowEmojisSelector] = useState(false);
  const [editingComment, setEditingComment] = useState(false);
  const [idOfCommentCurrentlyBeingEdited, setIdOfCommentCurrentlyBeingEdited] =
    useState(null);
  const [formValues, setFormValues] = useState({
    name: "",
    uid: null,
    isAdmin: false,
    comment: "",
  });
  const { firebase } = useAuth();
  const inputRef = useRef();
  
  useEffect(() => {
    const existingUser = JSON.parse(
      window.localStorage.getItem("parkinsons2026ExistingUser")
    );
    if (existingUser) {
      setParticipate(true);
      setFormValues({
        ...formValues,
        name: existingUser.name,
        uid: existingUser.uid,
        isAdmin: false,
      });
    }
  }, []);

  useEffect(() => {
    let unsubscribeFromUser;
    if (firebase && formValues.uid !== null) {
      unsubscribeFromUser = firebase.subscribeToUser({
        snapshot: (snapshot) => {
          if (!snapshot.empty) {
            setFormValues({
              ...formValues,
              name: snapshot.data()?.name,
              isAdmin: snapshot.data()?.isAdmin,
            });
          } else if (snapshot.empty) {
            console.log("No user account created");
          }
        },
        uid: formValues.uid,
      });
    }
    return () => {
      if (unsubscribeFromUser) {
        unsubscribeFromUser();
      }
    };
  }, [firebase, formValues.uid]);

  useEffect(() => {
    let unsubscribeFromComments;
    if (firebase) {
      unsubscribeFromComments = firebase.subscribeToComments({
        snapshot: (snapshot) => {
          console.log(snapshot);
          if (!snapshot.empty) {
            const _allComments = [];
            const _allCommentsToGenerateCSV = [];
            snapshot.forEach((doc) => {
              _allComments.push({
                cid: doc.id,
                ...doc.data(),
              });
              // const { name, text, timestamp } = doc.data();
              // _allCommentsToGenerateCSV.push({
              //   Name: name?.replace(/,/g, ""),
              //   Comment: text?.replace(/,/g, ""),
              // });
            });
            setAllComments(_allComments);
            // setAllCommentsToGenerateCSV(_allCommentsToGenerateCSV);
          } else if (snapshot.empty) {
            console.log("Nothing");
            setAllComments([]);
          }
        },
      });
    }
    return () => {
      if (unsubscribeFromComments) {
        unsubscribeFromComments();
      }
    };
  }, [firebase]);

  // console.log(allCommentsToGenerateCSV);

  const handleGenerateCommentsCSV = (
    event,
    filename = "United-For-Parkinsons-Comments"
  ) => {
    event.preventDefault();

    let csvFile;

    for (let row = 0; row < allCommentsToGenerateCSV.length; row++) {
      let keysAmount = Object.keys(allCommentsToGenerateCSV[row]).length;
      let keysCounter = 0;
      if (row === 0) {
        for (let key in allCommentsToGenerateCSV[row]) {
          csvFile += key + (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter++;
        }
      } else {
        for (let key in allCommentsToGenerateCSV[row]) {
          csvFile +=
            allCommentsToGenerateCSV[row][key] +
            (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter++;
        }
      }

      keysCounter = 0;
    }
    console.log(csvFile);
    const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleTextInputChange = (event) => {
    event.persist();
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmitInput = (event) => {
    event.preventDefault();
    if (formValues.uid === null) {
      if (formValues.name !== "" && formValues.name !== " ") {
        firebase
          .createUserInDatabase({
            name: formValues.name,
          })
          .then((result) => {
            setFormValues({
              ...formValues,
              uid: result,
            });
            window.localStorage.setItem(
              "parkinsons2026ExistingUser",
              JSON.stringify({
                name: formValues.name,
                uid: result,
              })
            );
          })
          .catch(console.error);
        setParticipate(true);
      }
    } else {
      if (formValues.comment !== "" && formValues.comment !== " ") {
        if (!editingComment) {
          firebase
            .submitNewComment({
              name: formValues.name,
              uid: formValues.uid,
              text: formValues.comment,
            })
            .then(() => {
              setFormValues({
                ...formValues,
                comment: "",
              });
            })
            .catch(console.error);
        } else {
          firebase
            .editComment({
              cid: idOfCommentCurrentlyBeingEdited,
              uid: formValues.uid,
              updatedText: formValues.comment,
            })
            .then(() => {
              resetEditingCommentState();
            })
            .catch(console.error);
        }
      }
    }
  };

  const resetEditingCommentState = () => {
    setEditingComment(false);
    setIdOfCommentCurrentlyBeingEdited(null);
    setFormValues({
      ...formValues,
      comment: "",
    });
  };

  const handleDeleteComment = ({ cid, uid }) => {
    firebase.deleteComment({ cid, uid });
  };

  const handleEditComment = ({ cid, text }) => {
    if (editingComment && cid === idOfCommentCurrentlyBeingEdited) {
      resetEditingCommentState();
    } else {
      setEditingComment(true);
      setIdOfCommentCurrentlyBeingEdited(cid);
      setFormValues({
        ...formValues,
        comment: text,
      });
    }
  };

  const handlePinComment = ({ cid, isPinned, uid }) => {
    if (editingComment && cid !== idOfCommentCurrentlyBeingEdited) {
      resetEditingCommentState();
    }
    if (isPinned) {
      firebase.unpinComment({ uid, cid, uid });
    } else {
      firebase.pinComment({ uid, cid, uid });
    }
  };

  const handleEmojiClick = (emojiType) =>
    firebase.sendEmoji({ emojiType }).catch(console.error);

  return (
    <Container>
      {participate ? (
        <EventComments>
          {allComments.length > 0 ? (
            <AnimatePresence initial={false}>
              {allComments.map((comment) => (
                <Comment
                  layout
                  key={comment.cid}
                  isPinned={comment.pinned.status}
                  initial={{
                    scale: 0,
                    opacity: 0,
                  }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    transition: {
                      duration: 0.9,
                      type: "spring",
                    },
                  }}
                  exit={{
                    scale: 0,
                    opacity: 0,
                    transition: {
                      duration: 0.6,
                      type: "tween",
                    },
                  }}
                >
                  <CommentTextAndButtons>
                    <p>
                      <span>{comment.name}</span>
                      <Interweave
                        content={comment.text}
                        matchers={[
                          new UrlMatcher("url"),
                          new MentionsMatcher("mention"),
                          new EmailMatcher("email"),
                        ]}
                        tagName="q"
                        newWindow
                      />
                    </p>
                    {formValues.uid === comment.uid && (
                      <>
                        <DeleteOrEditComment
                          whileTap={{ scale: 0.9 }}
                          style={{ marginLeft: "0" }}
                          onClick={() =>
                            handleDeleteComment({
                              cid: comment.cid,
                              uid: formValues.uid,
                            })
                          }
                        >
                          Delete
                        </DeleteOrEditComment>
                        <DeleteOrEditComment
                          whileTap={{ scale: 0.9 }}
                          onClick={() =>
                            handleEditComment({
                              cid: comment.cid,
                              text: comment.text,
                            })
                          }
                          style={{
                            opacity:
                              editingComment &&
                              comment.cid === idOfCommentCurrentlyBeingEdited
                                ? 0.4
                                : 1,
                          }}
                        >
                          Edit
                        </DeleteOrEditComment>
                      </>
                    )}
                    {formValues.isAdmin && formValues.uid !== comment.uid && (
                      <DeleteOrEditComment
                        style={{ marginLeft: "0" }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          handleDeleteComment({
                            cid: comment.cid,
                            uid: comment.cid.split("_")[0],
                          })
                        }
                      >
                        Delete
                      </DeleteOrEditComment>
                    )}
                    {formValues.isAdmin && (
                      <PinComment
                        isPinned={comment.pinned.status}
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          handlePinComment({
                            cid: comment.cid,
                            isPinned: comment.pinned.status,
                            uid: comment.cid.split("_")[0],
                          })
                        }
                      >
                        <Pin
                          stroke={comment.pinned?.status ? "#2a6440" : "#000"}
                          fill={
                            comment.pinned?.status ? "#2a6440" : "transparent"
                          }
                        />
                        {comment.pinned?.status ? "Unpin" : "Pin"}
                      </PinComment>
                    )}
                  </CommentTextAndButtons>
                </Comment>
              ))}
            </AnimatePresence>
          ) : (
            <p
              style={{
                color: "#000",
                paddingTop: "1.25rem",
                textAlign: "left",
                fontStyle: "italic",
              }}
            >
              Add your comment below....
            </p>
          )}
        </EventComments>
      ) : (
        <EventComments>
          <p
            style={{
              color: "#000",
              paddingTop: "1.25rem",
              textAlign: "center",
              margin: "auto 0",
              fontStyle: "italic",
            }}
          >
            Enter your name in the field below
            <br /> to join the chat
          </p>
        </EventComments>
      )}
      <CommentForm onSubmit={handleSubmitInput} autoComplete="off">
        <AddComment>
          {formValues.uid === null ? (
            <NameInput
              onChange={handleTextInputChange}
              value={formValues.name}
              id="name"
              name="name"
              type="text"
              placeholder="Enter your name here..."
            />
          ) : (
            <AnimatePresence initial={false}>
              <CommentInput
                ref={inputRef}
                onChange={handleTextInputChange}
                value={formValues.comment}
                id="comment"
                name="comment"
                type="text"
                placeholder="Type your message..."
              />
            </AnimatePresence>
          )}
          <Buttons>
            {!participate ? (
              <Participate
                type="submit"
                disabled={formValues.name === "" || formValues.name === " "}
              >
                <p>Participate</p>
              </Participate>
            ) : (
              <AnimatePresence>
                <SubmitComment
                  type="submit"
                  disabled={
                    formValues.comment === "" || formValues.comment === " "
                  }
                >
                  <CommentArrow width="1rem" />
                </SubmitComment>
                <Emojis
                  onTap={() =>
                    isMobile &&
                    setShowEmojisSelector((currentValue) => !currentValue)
                  }
                  onHoverStart={() => setShowEmojisSelector(true)}
                  onHoverEnd={() => setShowEmojisSelector(false)}
                >
                  <AnimatePresence>
                    {showEmojisSelector && (
                      <EmojiSelector
                        variants={fadeInAndOutVariants({
                          exitDelay: 0.1,
                        })}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <div>
                          <Emoji onClick={() => handleEmojiClick("smile")}>
                            <EmojiSmile />
                          </Emoji>
                          <Emoji onClick={() => handleEmojiClick("love")}>
                            <EmojiLove />
                          </Emoji>
                          <Emoji onClick={() => handleEmojiClick("like")}>
                            <EmojiLike />
                          </Emoji>
                          <Emoji onClick={() => handleEmojiClick("clap")}>
                            <EmojiClap />
                          </Emoji>
                          <div />
                        </div>
                      </EmojiSelector>
                    )}
                  </AnimatePresence>
                  <ThumbsUp width="1.25rem" />
                </Emojis>
              </AnimatePresence>
            )}
          </Buttons>
        </AddComment>
      </CommentForm>
     {/* Change to your Firebase user doc id*/}
      {formValues.uid === "FBrQnIouJr3At3TwTycO" && (
        <button
          className="generate"
          type="button"
          onClick={handleGenerateCommentsCSV}
        >
          Generate Event Comments CSV
        </button>
      )}
    </Container>
  );
};

const Container = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
})`
  background-color: #fff;
  position: relative;
  .generate {
    background: transparent;
    border: 1px solid var(--abbvie-green);
    border-radius: 2px;
    bottom: -8%;
    cursor: pointer;
    padding: 0.5rem 1rem;
    position: absolute;
    right: 0;
    transition: all 0.3s ease-in-out,;
    z-index: 5;
    &:hover {
      background: var(--abbvie-green);
      color: #fff;
    }
  }
`;

const AddComment = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const CommentForm = styled.form`
  box-sizing: border-box;
  background: white;
  border-radius: 0 0 2px 2px;
  bottom: 0;
  height: auto;
  left: 0;
  padding: 1rem;
  position: absolute;
  width: 100%;
  z-index: 1;
  :before {
    content: "";
    background: #c4c4c4;
    position: absolute;
    margin: 0 1rem;
    top: 0;
    left: 0;
    height: 1px;
    width: calc(100% - 2rem);
  }
`;

const EventComments = styled.div`
  border-top: none;
  display: flex;
  flex-direction: column;
  grid-column: 1/7;
  height: calc(100% - 4.5rem);
  min-height: 18.75rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 1rem;
  width: 100%;
  ::-webkit-scrollbar {
    background-color: rgba(196, 196, 196, 0.2);
    width: 0.875rem;
  }
  ::-webkit-scrollbar-track {
    border-radius: 0;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #c4c4c4;
    border-radius: 0.625rem;
  }
`;

const Comment = styled(motion.div)`
  align-items: flex-start;
  background-color: ${({ isPinned }) =>
    isPinned ? "rgba(42, 100, 64, 0.1)" : "transparent"};
  border-radius: 2px;
  color: #000;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.7em;
  padding: 0.125em 0.25em 0;
  &:first-of-type {
    margin-top: 1.25rem;
  }
  > div {
    bottom: 0.375rem;
    position: relative;
    p {
      color: #000;
      font-size: 1rem;
      line-height: 1.25em;
      margin-top: 0.575em;
      q {
        &:before {
          content: "";
        }
        &:after {
          content: "";
        }
      }
      span {
        cursor: pointer;
        color: ${({ isPinned }) =>
          isPinned ? "rgba(42, 100, 64, 1)" : "#C4C4C4"};
        font-weight: 600;
        margin-right: 0.475rem;
      }
      small {
        justify-self: end;
        bottom: 0.125em;
        display: inline-block;
        font-size: 0.65rem;
        left: 0.063em;
        position: relative;
        &:last-of-type {
          color: #000;
        }
      }
    }
  }
`;

const CommentTextAndButtons = styled.div`
  p {
    color: black;
  }
  a {
    color: black;
    text-decoration: underline;
  }
  div {
    margin-left: auto;
  }
`;

const DeleteOrEditComment = styled(motion.small)`
  cursor: pointer;
  margin-left: 0.5em;
  text-decoration: underline;
`;

export const PinComment = styled(DeleteOrEditComment)`
  margin-left: 2.6em;
  position: relative;
  > svg {
    bottom: -0.1em;
    height: 1.2em;
    left: -1.45em;
    position: absolute;
    width: 1.2em;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  width: 6.25rem;
  > * {
    margin-left: 0.625rem;
  }
`;

const NameInput = styled.input`
  align-items: center;
  background-color: transparent;
  border: 1px solid #c4c4c4;
  border-bottom: 1px solid;
  border-color: #c4c4c4;
  border-radius: 2px;
  color: black;
  display: flex;
  font-size: 1rem;
  font-weight: 400;
  height: 2.5rem;
  justify-content: flex-start;
  letter-spacing: 0.5px;
  line-height: 2rem;
  outline-color: #c4c4c4;
  padding: 0.625rem;
  width: calc(100% - 8rem);
  &:focus {
    outline-color: var(--abbvie-green);
  }
  ::placeholder {
    color: #2d2d2d;
    font-size: 1rem;
    font-style: italic;
    font-weight: 400;
  }
`;

const CommentInput = styled.input`
  align-items: center;
  background-color: transparent;
  border: 1px solid #c4c4c4;
  border-bottom: 1px solid;
  border-color: #c4c4c4;
  border-radius: 2px;
  color: black;
  display: flex;
  font-size: 1rem;
  font-weight: 400;
  height: 2.5rem;
  justify-content: flex-start;
  letter-spacing: 0.5px;
  line-height: 2rem;
  outline-color: #c4c4c4;
  padding: 0.625rem;
  width: calc(100% - 6.25rem);
  &:focus {
    outline-color: var(--abbvie-green);
  }
  ::placeholder {
    color: #2d2d2d;
    font-size: 1rem;
    font-style: italic;
    font-weight: 400;
  }
`;

const Participate = styled.button`
  position: relative;
  align-items: center;
  background-color: ${({ disabled }) =>
    disabled ? "rgba(196, 196, 196, 0.3)" : "#2a6440"};
  color: ${({ disabled }) => (disabled ? "#C4C4C4" : "white")};
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  height: 2.5rem;
  justify-content: center;
  transition: background-color 0.25s ease-in-out, stroke 0.25s ease-in-out,
    outline-color 0.25s ease-in-out;
  p {
    text-transform: uppercase;
    font-weight: bold;
    padding: 0 1rem;
  }
`;

const SubmitComment = styled.button`
  align-items: center;
  background-color: ${({ disabled }) =>
    disabled ? "rgba(196, 196, 196, 0.3)" : "#2a6440"};
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  height: 2.5rem;
  justify-content: center;
  transition: background-color 0.25s ease-in-out, stroke 0.25s ease-in-out,
    outline-color 0.25s ease-in-out;
  width: 2.5rem;
  svg path {
    stroke: #fff;
  }
  &:focus {
    outline-color: black;
  }
`;

const EmojiSelector = styled(motion.div)`
  height: 3.25rem;
  position: absolute;
  right: -0.063rem;
  top: -3.125rem;
  width: 10rem;
  > div {
    background-color: #fff;
    box-shadow: 0px 0.25rem 0.625rem rgba(0, 0, 0, 0.14);
    display: flex;
    height: 2.5rem;
    justify-content: space-between;
    padding: 0.5rem 0.688rem;
    position: relative;
    > div {
      background-color: #fff;
      bottom: -0.25rem;
      height: 0.5rem;
      pointer-events: none;
      position: absolute;
      right: 0.875rem;
      transform: rotate(45deg);
      width: 0.5rem;
    }
  }
`;

const Emojis = styled(motion.div)`
  align-items: center;
  background-color: #fff;
  border: 1px solid var(--abbvie-green);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  height: 2.5rem;
  justify-content: center;
  position: relative;
  transition: background-color 0.25s ease-in-out, fill 0.25s ease-in-out;
  width: 2.5rem;
  &:hover {
    background-color: var(--abbvie-green);
    > svg path {
      fill: #fff;
    }
  }
`;

const Emoji = styled(motion.span).attrs({
  whileTap: {
    scale: 0.9,
  },
  whileHover: {
    scale: 1.1,
  },
})`
  display: inline-flex;
  svg {
    width: 1.5rem;
  }
`;

export default Chat;
